/**
 * Перенос `data/app_repository_impl.dart`.
 *
 * Riverpod в порте нет, поэтому `appRepositoryProvider` заменён на
 * [appRepository] — один общий экземпляр поверх общего `httpClient`.
 * Кому нужен свой транспорт (фоновая цепочка `fetchAppState`, тесты),
 * конструирует класс сам: `new AppRepositoryImpl({ client: getBaseClient() })`.
 */

import { Endpoints } from '@/config/endpoints';
import { TabletSessionManager } from '@/data/tabletSessionManager';
import { apiResponseModelFromResponse } from '@/domain/models/apiResponseModel';
import { configModelFromJson, type ConfigModel } from '@/domain/models/configModel';
import {
  PERSON_MOOD_STATE_PARAM,
  personMoodModelFromJson,
  type PersonMoodModel,
  type PersonMoodState,
} from '@/domain/models/personMoodModel';
import { publicTabletFromJson, type PublicTablet } from '@/domain/models/publicTablet';
import type { TabletConfigModel } from '@/domain/models/tabletConfigModel';
import type { AppRepository } from '@/domain/repository/appRepository';
import { AppException, stackTraceOf } from '@/utils/exceptions/appException';
import { httpClient, type HttpClient } from '@/utils/http/httpClient';
import { asRecord } from '@/utils/http/types';
import { StorageHelper } from '@/utils/storageHelper';

/** Больше 100 за раз сервер не отдаёт — дочитываем страницами. */
const PUBLIC_TABLETS_PAGE_SIZE = 100;

/** Аргументы конструктора [AppRepositoryImpl]. */
export interface AppRepositoryImplInit {
  readonly client: HttpClient;
}

export class AppRepositoryImpl implements AppRepository {
  constructor({ client }: AppRepositoryImplInit) {
    this.client = client;
  }

  readonly client: HttpClient;

  async checkEndpoint(baseUrl: string): Promise<boolean> {
    try {
      await this.fetchConfig(baseUrl);
      return true;
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async fetchConfig(baseUrl: string): Promise<ConfigModel> {
    try {
      const response = await this.client.get(baseUrl);
      const apiResponse = apiResponseModelFromResponse<ConfigModel>(response, (item) =>
        configModelFromJson(item),
      );
      const config = requireConfig(apiResponse.data);
      // Полные STT (api_key) живут в защищённой конфигурации планшета.
      // null — её сейчас не получить: старый бекенд, сессия истекла (PIN
      // без пароля, прошло 24 часа), нет сети. Тогда ключи того же планшета,
      // полученные раньше, остаются из кеша — иначе голос отвалится с 401.
      // Креды адреса на месте, пока сервер не отверг сессию окончательно.
      // Их нет — чужой адрес (проба на экране адреса), сознательное «без
      // пароля» или стёртая мёртвая сессия: кеш не переносим, ключи вернёт
      // следующий вход по паролю.
      const storage = StorageHelper.instance;
      const tabletConfig = await TabletSessionManager.instance.tryFetchTabletConfig(baseUrl);
      const enriched = enrichWithStt(config, tabletConfig, baseUrl, storage);
      await storage.saveConfig(enriched);
      return enriched;
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async fetchPublicTablets(serverRoot: string): Promise<readonly PublicTablet[]> {
    try {
      // `tablets` не мутируется: каждая страница даёт новый список.
      let tablets: readonly PublicTablet[] = [];
      while (true) {
        const response = await this.client.get(Endpoints.publicTablets(serverRoot), {
          queryParameters: { offset: tablets.length, limit: PUBLIC_TABLETS_PAGE_SIZE },
        });
        // `response.data as Map<String, dynamic>` в Dart бросает `TypeError`,
        // когда тело не объект; слой исключений разбирает его так же.
        const json = asRecord(response.data);
        if (json === null) throw new TypeError('tablets/public: тело ответа не объект');
        const items = parsePublicTabletPage(json['items']);
        tablets = [...tablets, ...items];
        const total = json['total'];
        if (typeof total !== 'number') {
          throw new TypeError('tablets/public: поле total не число');
        }
        if (items.length === 0 || tablets.length >= total) {
          return tablets;
        }
      }
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async checkPincode(pincode: number): Promise<boolean> {
    try {
      const response = await this.client.post(Endpoints.checkPinCode, {
        data: {
          code: pincode,
        },
      });
      // Новый бекенд к верному PIN прикладывает токены планшета — те же,
      // что у tablets/auth. Запрос ушёл на ${baseUrl}, им токены и
      // принадлежат. Старый бекенд полей не отдаёт — работаем как раньше.
      const body = asRecord(response.data);
      const baseUrl = StorageHelper.instance.baseUrl;
      if (body !== null && baseUrl !== null) {
        const tokens = pinTokens(body);
        if (tokens !== null) {
          await TabletSessionManager.instance.adoptPinTokens({
            baseUrl,
            token: tokens.token,
            refreshToken: tokens.refreshToken,
          });
        }
      }
      return true;
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async fetchPersonMoodState(baseUrl: string): Promise<PersonMoodModel> {
    try {
      const response = await this.client.get(Endpoints.personMoodState(baseUrl));
      return personMoodModelFromJson(response.data);
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }

  async changePersonMood(baseUrl: string, moodState: PersonMoodState): Promise<PersonMoodModel> {
    try {
      const response = await this.client.post(Endpoints.personMoodState(baseUrl), {
        data: {
          mood_state: PERSON_MOOD_STATE_PARAM[moodState],
        },
      });
      return personMoodModelFromJson(response.data);
    } catch (e) {
      throw AppException.handle(e, stackTraceOf(e));
    }
  }
}

/**
 * `apiResponse.data!` из Dart: `null` в `data` — ответ не по контракту.
 * Оператор `!` бросал там `TypeError`, слой исключений разбирает его так же
 * (`typeError` -> `badContract`).
 */
function requireConfig(config: ConfigModel | null): ConfigModel {
  if (config === null) throw new TypeError('в ответе конфигурации нет поля data');
  return config;
}

/**
 * Ветки выбора конфигурации из `fetchConfig` один в один: защищённая
 * конфигурация -> слияние ключей, иначе свой адрес с сохранёнными кредами ->
 * перенос кеша, иначе открытый ответ как есть.
 */
function enrichWithStt(
  config: ConfigModel,
  tabletConfig: TabletConfigModel | null,
  baseUrl: string,
  storage: StorageHelper,
): ConfigModel {
  if (tabletConfig !== null) {
    return TabletSessionManager.mergeStt(config, tabletConfig);
  }
  if (storage.isCurrentBaseUrl(baseUrl) && storage.credentialsMatch(baseUrl)) {
    return TabletSessionManager.keepCachedStt(config, storage.config);
  }
  return config;
}

/**
 * `(json['items'] as List).map(PublicTablet.fromJson)`. Не список — тело не
 * по контракту, и в Dart это был тот же `TypeError` от приведения типа.
 */
function parsePublicTabletPage(items: unknown): readonly PublicTablet[] {
  if (!Array.isArray(items)) throw new TypeError('tablets/public: поле items не список');
  return items.map((item: unknown) => publicTabletFromJson(item));
}

/** Пара токенов из ответа `check-pin`, если бекенд их прислал. */
interface PinTokens {
  readonly token: string;
  readonly refreshToken: string;
}

/**
 * `token` и `refresh_token` из тела ответа: оба должны быть непустыми
 * строками, иначе это старый бекенд — токенов нет.
 */
function pinTokens(body: Readonly<Record<string, unknown>>): PinTokens | null {
  const token = body['token'];
  const refreshToken = body['refresh_token'];
  if (
    typeof token !== 'string' ||
    token.length === 0 ||
    typeof refreshToken !== 'string' ||
    refreshToken.length === 0
  ) {
    return null;
  }
  return { token, refreshToken };
}

/** Аналог `appRepositoryProvider`: репозиторий на общем клиенте. */
export const appRepository: AppRepository = new AppRepositoryImpl({
  client: httpClient,
});
